import React, { useEffect, useMemo, useState } from 'react';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';

import { ProductKeys } from '../../../common/constants/products';
import { searchResultsLoadUFOExperience } from '../../../common/ui/ufo/experiences';
import { isProduct3P } from '../../../common/utils/product';
import { useSelectedFilters } from '../../../controllers/filters/selected';
import { useSafeIntercom } from '../../../controllers/intercom';
import { EmptyEntitiesError } from '../../../controllers/results/fetch-results';
import {
	useBootstrap,
	useIsLoading,
	useResults,
	useResultsError,
	useResultsRequestId,
	useSearchActions,
	useSelectedProduct,
} from '../../../controllers/store';
import { Error } from './error';
import ErrorDark from './error/assets/error-dark.svg';
import ErrorLight from './error/assets/error-light.svg';
import { darkImageKey, lightImageKey, manageImageCaching } from './error/image-cache';
import { Landing } from './landing';
import { Loading } from './loading';
import { NoResults } from './no-results';
import { Results } from './results';

const resultCountAndSortContainer = xcss({
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
	marginBottom: 'space.050',
});

const ResultsState = {
	NO_RESULTS: 'noResults',
	INITIAL: 'initial',
	RESULTS: 'results',
	ERROR: 'error',
	IS_LOADING: 'isLoading',
	EMPTY: 'empty',
} as const;

/**
 * A hook to determine the state of search results on a search page.
 *
 * NOTE: It is possible for SearchPageQuery to contain search results AND errors within the GraphQL
 * response. In this case, it is expected for FPS to display search search results even if there are
 * downstream errors. To prevent this issue, `ResultsState.RESULTS` state should ALWAYS be above
 * `ResultsState.ERROR`. See PIR-21043 for the investigation.
 *
 * Example:
 * ```ts
 * if (results && results.edges.length > 0) {
 * 	return { state: ResultsState.RESULTS };
 * }
 *
 * if (error) {
 * 	return { state: ResultsState.ERROR };
 * }
 * ```
 */
export const useResultsState = ():
	| {
			state: 'isLoading';
	  }
	| {
			state: 'empty';
	  }
	| {
			state: 'initial';
	  }
	| {
			state: 'results';
	  }
	| {
			state: 'noResults';
	  }
	| {
			state: 'error';
	  } => {
	// TODO: this hook should be moved to the Store, so it can be used by other components.
	const [{ outboundAuthConfigs }] = useBootstrap();
	const { hasFiltersSelected } = useSelectedFilters({
		outboundAuthConfigs,
	});
	const [isLoading] = useIsLoading();
	const [error] = useResultsError();
	const [results] = useResults();

	return useMemo(() => {
		// This narrows the result down to either loading or nothing
		if (results === undefined && error === undefined) {
			// In this case, we know user has selected filters, so we can show loading state
			if (hasFiltersSelected) {
				return { state: ResultsState.IS_LOADING };
			}

			/*
				In some cases, isLoading returns true before hasFiltersSelected returns true
				In these cases, we do not know if:
				- the user has selected filters (we should show loading state)
				- the user has not selected filters (we should not show loading state)

				For this reason, we display nothing until we know filter state for certain
			 */
			return { state: ResultsState.EMPTY };
		}

		// In this case, we know user has not selected filters, so we can show initial state
		if (!hasFiltersSelected) {
			return { state: ResultsState.INITIAL };
		}

		// This covers loading case where a second query was made
		if (isLoading) {
			return { state: ResultsState.IS_LOADING };
		}

		if (results && results.edges.length > 0) {
			return { state: ResultsState.RESULTS };
		}

		// If there are no entities for a query, an error will throw
		// when no entities for query is equivalent to no results
		if (error instanceof EmptyEntitiesError || (results && results.edges.length === 0)) {
			return { state: ResultsState.NO_RESULTS };
		}

		return { state: ResultsState.ERROR };
	}, [hasFiltersSelected, isLoading, error, results]);
};

export const ResultsContainer = (): React.JSX.Element | null => {
	const [results] = useResults();
	const [requestId] = useResultsRequestId();
	const [error] = useResultsError();
	const [selectedProduct] = useSelectedProduct();
	const { retrySearch } = useSearchActions();
	const intercom = useSafeIntercom();
	const { state } = useResultsState();

	const [isOnline, _] = useState(navigator.onLine);

	useEffect(() => {
		if (results && results.edges.length >= 0) {
			searchResultsLoadUFOExperience.mark('render_end');
			searchResultsLoadUFOExperience.success({
				metadata: { productFilter: selectedProduct, requestId },
			});

			// Only fire intercom results shown events when there are actually results
			// as we don't know when the user has connected their 3P provider or not
			if (results.edges.length > 0) {
				if (selectedProduct && isProduct3P(selectedProduct)) {
					intercom?.trackEvent('search_results_shown_3p', {
						product_filter: selectedProduct,
					});
				} else {
					intercom?.trackEvent('search_results_shown', {
						product_filter: selectedProduct,
					});
				}
			}
		} else if (error && !results) {
			searchResultsLoadUFOExperience.failure({
				metadata: { productFilter: selectedProduct },
			});
		}
	}, [results, selectedProduct, error, intercom, requestId]);

	useEffect(() => {
		manageImageCaching(isOnline, ErrorLight, lightImageKey, ErrorDark, darkImageKey);
	}, [isOnline]);

	const showResultCountAndSort = useMemo(
		() => selectedProduct === ProductKeys.Confluence,
		[selectedProduct],
	);

	if (state === ResultsState.IS_LOADING) {
		return <Loading />;
	}

	if (state === ResultsState.INITIAL) {
		return <Landing />;
	}

	if (state === ResultsState.RESULTS) {
		return (
			<Box>
				{showResultCountAndSort && (
					<Box xcss={resultCountAndSortContainer}>
						<Box>{/* TODO: Add result count component here */}</Box>
					</Box>
				)}
				<Results />
			</Box>
		);
	}

	if (state === ResultsState.NO_RESULTS) {
		return (
			<>
				<NoResults />
			</>
		);
	}

	if (state === ResultsState.EMPTY) {
		return null;
	}

	return <Error fetchResults={retrySearch} />;
};
